// ngie author tables (`table.custom_author_table`) can be wider than a
// printable page. Left alone the overflowing columns run off the side of the
// page and are lost, so this pass runs before the pager chunks the document and
// packs each wide table's columns into groups that fit within the available
// page width: the columns that fit stay in the original table, and every
// remaining group of columns is moved into a follow-on table rendered directly
// below (which the vertical pager then flows onto the next page as needed).

const SPLIT_TABLE_CLS = 'print-split-table'

// Trimmed off the measured container width so the last column that "just fits"
// isn't clipped by sub-pixel rounding or the table's border-spacing.
const WIDTH_SAFETY_MARGIN = 2

// Vertical gap rendered between a wide table and each follow-on group stacked
// below it, so the split groups don't read as one continuous table.
const SPLIT_TABLE_GAP = '1em'

export default function splitWideTables(root, fallbackWidth) {
  if (!root) {
    return
  }
  Array.from(root.querySelectorAll('table.custom_author_table')).forEach((table) => {
    splitTable(table, fallbackWidth)
  })
}

function splitTable(table, fallbackWidth) {
  // Cells that span multiple columns/rows make index-based column removal
  // unsafe, so leave those tables untouched.
  if (table.querySelector('[colspan], [rowspan]')) {
    return
  }

  // The table renders inside a nested item card that is narrower than the page,
  // so measure the space actually available to it from its own container rather
  // than the full page width.
  const availableWidth = availableWidthFor(table, fallbackWidth)
  if (!(availableWidth > 0)) {
    return
  }

  const columnWidths = measureColumnWidths(table)
  if (columnWidths.length <= 1) {
    return
  }

  const totalWidth = columnWidths.reduce((sum, width) => sum + width, 0)
  if (totalWidth <= availableWidth) {
    return
  }

  const groups = groupColumns(columnWidths, availableWidth)
  if (groups.length <= 1) {
    return
  }

  // Clone the untouched table up front: trimming the original for the first
  // group must not affect the columns the follow-on tables are built from.
  const pristine = table.cloneNode(true)

  let insertAfter = table
  groups.forEach((columnIndices, groupIndex) => {
    if (groupIndex === 0) {
      keepOnlyColumns(table, columnIndices)
      resetTableWidth(table)
      table.classList.add(SPLIT_TABLE_CLS)
      // Records this group's position among the stacked split tables. When the
      // vertical pager breaks these siblings across pages it works bottom-up and
      // would otherwise place a lower group's continuation ahead of a higher
      // one's; breakTable (render_flexing_item) uses this to keep continuations
      // in group order so the columns read left-to-right across pages.
      table.dataset.splitOrder = groupIndex
      return
    }

    const clone = pristine.cloneNode(true)
    keepOnlyColumns(clone, columnIndices)
    resetTableWidth(clone)
    clone.classList.add(SPLIT_TABLE_CLS)
    clone.dataset.splitOrder = groupIndex
    clone.style.marginTop = SPLIT_TABLE_GAP
    insertAfter.parentNode.insertBefore(clone, insertAfter.nextSibling)
    insertAfter = clone
  })
}

// The width available to the table is the content box of its containing card,
// not the full page. `clientWidth` already excludes the parent's border, so we
// subtract the parent's L/R padding, the table's own L/R border (which eats into
// the space the columns can use) and a small safety margin. Falls back to the
// page width if the container cannot be measured (e.g. it or an ancestor is
// hidden at pre-pass time).
function availableWidthFor(table, fallbackWidth) {
  const parent = table.parentElement
  if (!parent) {
    return fallbackWidth
  }
  const parentStyles = getComputedStyle(parent)
  const tableStyles = getComputedStyle(table)
  const width =
    parent.clientWidth -
    (parseFloat(parentStyles.paddingLeft) || 0) -
    (parseFloat(parentStyles.paddingRight) || 0) -
    (parseFloat(tableStyles.borderLeftWidth) || 0) -
    (parseFloat(tableStyles.borderRightWidth) || 0) -
    WIDTH_SAFETY_MARGIN
  return width > 0 ? width : fallbackWidth
}

// Measures each column's preferred width by laying the table out with its
// natural width off-screen, unconstrained by the print clipping styles.
function measureColumnWidths(table) {
  const headerCells = columnCells(table)
  if (headerCells.length === 0) {
    return []
  }

  const measurer = document.createElement('div')
  measurer.style.cssText = 'position: absolute; visibility: hidden; left: -99999px; top: 0;'

  const clone = table.cloneNode(true)
  clone.style.cssText = 'display: table; table-layout: auto; width: auto; max-width: none; overflow: visible;'
  clone.removeAttribute('width')

  measurer.appendChild(clone)
  document.body.appendChild(measurer)
  const widths = columnCells(clone).map((cell) => cell.getBoundingClientRect().width)
  measurer.remove()

  return widths
}

// Greedily packs sequential columns into groups that each stay within the
// available width. A single column wider than the page still gets its own
// group rather than being dropped.
function groupColumns(columnWidths, availableWidth) {
  const groups = []
  let current = []
  let currentWidth = 0

  columnWidths.forEach((width, index) => {
    if (current.length > 0 && currentWidth + width > availableWidth) {
      groups.push(current)
      current = []
      currentWidth = 0
    }
    current.push(index)
    currentWidth += width
  })

  if (current.length > 0) {
    groups.push(current)
  }

  return groups
}

function keepOnlyColumns(table, keepIndices) {
  const keep = new Set(keepIndices)
  tableRows(table).forEach((row) => {
    // Snapshot children first so removals don't shift the indices we compare.
    Array.from(row.children).forEach((cell, index) => {
      if (!keep.has(index)) {
        cell.remove()
      }
    })
  })
}

function resetTableWidth(table) {
  table.style.removeProperty('width')
  table.removeAttribute('width')
}

// Header cells of the first row, used to determine the column count/order.
function columnCells(table) {
  const rows = tableRows(table)
  return rows.length > 0 ? Array.from(rows[0].children) : []
}

// Rows that belong directly to this table (ignores rows of any nested table).
function tableRows(table) {
  return Array.from(
    table.querySelectorAll(':scope > thead > tr, :scope > tbody > tr, :scope > tfoot > tr, :scope > tr'),
  )
}
